import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { BOX_IMAGES } from '@/data/images';

const EASE = [0.22, 1, 0.36, 1];

const CARDS = [
  { val: 'WOD', sub: 'Workout Of the Day', desc: 'Un challenge différent chaque jour' },
  { val: 'HYROX', sub: 'Training Club', desc: 'Préparation compétition fitness' },
  { val: 'TEAM', sub: "Esprit d'équipe", desc: 'Entraînements collectifs motivants' },
  { val: 'FREE', sub: 'Access', desc: 'Entraînez-vous en autonomie' },
];

export const CrossFitIntro = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);

  return (
    <section
      ref={ref}
      data-testid="crossfit-intro"
      className="relative overflow-hidden bg-dark"
    >
      {/* Parallax bg */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img decoding="async"
          src={BOX_IMAGES.kettlebell}
          alt="Entraînement CrossFit en groupe avec kettlebells à Saint-Chamond"
          className="w-full h-[130%] object-cover opacity-25"
          loading="lazy"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-dark via-dark/95 to-dark/70" />
      <div className="absolute inset-0 mesh-gradient opacity-30 pointer-events-none" />

      {/* Giant outline text decoration */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.06 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
        className="absolute -top-8 -right-20 font-display text-[20rem] leading-none text-white whitespace-nowrap select-none pointer-events-none hidden lg:block"
      >
        CROSSFIT
      </motion.div>

      <div className="container-custom relative z-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <span className="text-primary-glow font-semibold text-xs tracking-[0.3em] uppercase">
              ━━ Qu'est-ce que le CrossFit ?
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white mt-4 mb-7 leading-[0.9]">
              PLUS QU'UN SPORT,
              <br />
              UN{' '}
              <span className="text-gradient-primary italic font-display inline-block pr-2">
                MODE DE VIE
              </span>
            </h2>
            <p className="text-white/75 text-base leading-relaxed mb-5">
              Le CrossFit est une méthode d'entraînement basée sur des mouvements
              fonctionnels variés, exécutés à haute intensité. Il combine
              gymnastique, haltérophilie et exercices cardio pour développer
              une condition physique complète.
            </p>
            <p className="text-white/55 text-sm leading-relaxed mb-10">
              Chez CrossFit Saint-Chamond, nous sommes aussi HYROX Training Club —
              un format de compétition alliant courses et exercices fonctionnels.
              Quel que soit votre niveau, nos coachs adaptent chaque séance à
              vos capacités.
            </p>
            <Link to="/programmes">
              <Button
                size="lg"
                className="btn-shine bg-primary hover:bg-primary-light text-white font-heading tracking-wider px-9 py-7 rounded-xl glow-primary group"
              >
                DÉCOUVRIR NOS PROGRAMMES
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>

          {/* Right: cards grid with stagger */}
          <div className="grid grid-cols-2 gap-4">
            {CARDS.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40, rotateX: -10 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true, margin: '-10%' }}
                transition={{
                  duration: 0.75,
                  delay: 0.15 + idx * 0.08,
                  ease: EASE,
                }}
                whileHover={{ y: -6, scale: 1.02 }}
                className="glass-card rounded-2xl p-6 hover:border-primary-glow/40 transition-colors duration-500 group"
              >
                <div className="font-display text-3xl text-primary-glow mb-1 group-hover:text-white transition-colors duration-300">
                  {item.val}
                </div>
                <div className="font-heading text-xs text-white/80 mb-3 tracking-wide">
                  {item.sub}
                </div>
                <p className="text-white/50 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
